import { Box, CircularProgress, Stack, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { Bound } from "lib/@components/binding/Bound"
import noop from "lib/noop"
import { navigate } from "lib/routes/navigate"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useCurrentUser } from "lib/store/use-current-user"
import { useDemand } from "lib/authorization/use-demand"
import { useDialog } from "lib/@hooks/useDialog"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { confirm } from "lib/@dialogs/confirm"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { useTreeItemIndex } from "routes/schedule/lib/useTreeItemIndex"
import { resolveCommentLocation } from "./resolve-comment-location"
import {
    deleteShareLinkScheduleComment,
    editShareLinkScheduleComment,
    getShareLinkRecipientUnread,
    getShareLinkRegimeComments,
    markShareLinkCommentThreadsRead,
    replyToShareLinkScheduleComment,
    resolveShareLinkScheduleComment,
} from "routes/schedule/plugins/share-link-comments/share-link-comments.controller"
import { filterShareLinkCommentThreads } from "routes/schedule/plugins/share-link-comments/share-link-comment-presenters"
import { useShareLinkCommentRegimeId } from "./use-share-link-comment-regime-id"
import { useShareLinkCommentReadTracker } from "./use-share-link-comment-read-tracker"
import { getDayGroups, getVisibleThreads } from "./share-link-comment-feed-helpers"
import { ShareLinkCommentDayGroup } from "./share-link-comment-day-group"
import { ShareLinkCommentEditDialog } from "./share-link-comment-edit-dialog"

const EMPTY_COMMENTS = []
const EMPTY_UNREAD = { total: 0, unreadThreadIds: [] }

// Recipient-facing Comments tab for the share regime view. Comments can only be left at schedule level
// or lower, so this is a read + reply aggregate of the recipient's schedule / task / introduction
// comment threads across the regime. New comments are created inline on the schedule items themselves
// (the task and introduction buttons, and the selection button) — there is no top-level composer here.
export function ShareLinkRecipientCommentsPanel() {
    const [shareLinkId] = useSearchParam("share", null)
    const [statusFilter] = useSearchParam("commentFilter", "open")
    const treeIndex = useTreeItemIndex()
    const regimeId = useShareLinkCommentRegimeId()
    const currentUser = useCurrentUser()
    const isShareLinkUser = useDemand("$sharing")
    const isRegimeEditor = useDemand("manageRegime")
    const openEditDialog = useDialog(<ShareLinkCommentEditDialog />)

    const rawResult = getShareLinkRegimeComments.useResults({ shareLinkId, regimeId }, withInitialValue(IS_LOADING))
    const viewer = { isShareLinkUser: isShareLinkUser && !isRegimeEditor, userId: currentUser.id }
    // A thread is marked read once its card has dwelled in view (see useMarkCommentThreadReadOnView on
    // the thread). The tracker batches those per-thread signals into one mark-read call and clears the
    // tab badge; new activity re-populates the unread query and re-arms its thread.
    const unread = getShareLinkRecipientUnread.useResults({ shareLinkId, regimeId }) ?? EMPTY_UNREAD
    const { unreadThreadSet, markThreadSeen } = useShareLinkCommentReadTracker({
        unreadThreadIds: unread.unreadThreadIds,
        mark: markReadThreads,
    })

    // Contained spinner inside the tab panel; LoadingScreen would raise the global full-page busy overlay
    if (rawResult === IS_LOADING) {
        return (
            <Box
                sx={{ display: "flex", justifyContent: "center", py: 4 }}
                data-cy="share-link-recipient-comments-loading"
            >
                <CircularProgress size={28} />
            </Box>
        )
    }

    const allThreads = (rawResult ?? EMPTY_COMMENTS).map(withCommentLocation)
    const comments = filterShareLinkCommentThreads(allThreads, viewer)
    const visibleThreads = getVisibleThreads(comments, { statusFilter, targetFilter: "all" })
    const dayGroups = getDayGroups(visibleThreads)

    return (
        <Stack spacing={1} data-cy="share-link-recipient-comments-panel" sx={{ flex: 1, minHeight: 0 }}>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                <Bound
                    schedule={undefined}
                    commentList={allThreads}
                    viewer={viewer}
                    markThreadSeen={markThreadSeen}
                    unreadThreadSet={unreadThreadSet}
                    onReply={reply}
                    onResolve={resolve}
                    onEdit={edit}
                    onDelete={remove}
                    onJump={jump}
                    onReveal={noop}
                >
                    <Repeat
                        list={dayGroups}
                        pass="group"
                        keyFn={getGroupKey}
                        item={<ShareLinkCommentDayGroup />}
                        fallback={
                            <Typography variant="body2" color="text.secondary">
                                No comments yet
                            </Typography>
                        }
                    />
                </Bound>
            </Box>
        </Stack>
    )

    function markReadThreads({ threadIds }) {
        if (!shareLinkId || !regimeId) return undefined
        return markShareLinkCommentThreadsRead({ shareLinkId, regimeId, threadIds })
    }

    // Attach the breadcrumb + schedule code to each comment's anchor, resolved from the loaded regime
    // tree, so the aggregated list shows where each comment sits.
    function withCommentLocation(thread) {
        const { location, code } = resolveCommentLocation(thread, treeIndex)
        if (!location && !code) return thread
        return { ...thread, anchor: { ...thread.anchor, location, code } }
    }

    function getGroupKey(group) {
        return group.key
    }

    // Jump to the comment's schedule page (schedule / task / introduction are the only comment kinds).
    function jump(comment) {
        const schedulePath = comment.schedulePath ?? comment.scheduleId
        if (schedulePath) navigate(`/app/facilities/schedule/${schedulePath}`)
    }

    async function reply(comment, body) {
        try {
            return await replyToShareLinkScheduleComment({
                shareLinkId: comment.shareLinkId ?? shareLinkId,
                regimeId,
                scheduleId: comment.scheduleId ?? null,
                threadId: comment.threadId ?? comment.id,
                content: body,
            })
        } catch (e) {
            errorSnackbar("Couldn't reply")
            return null
        }
    }

    async function resolve(comment, resolved) {
        try {
            await resolveShareLinkScheduleComment({
                shareLinkId: comment.shareLinkId ?? shareLinkId,
                regimeId,
                scheduleId: comment.scheduleId ?? null,
                threadId: comment.threadId ?? comment.id,
                resolved,
            })
        } catch (e) {
            errorSnackbar("Couldn't update the comment")
        }
    }

    async function remove(message) {
        try {
            if (!(await confirm("Delete this comment?"))) return
            await deleteShareLinkScheduleComment({
                shareLinkId: message.shareLinkId ?? shareLinkId,
                regimeId,
                scheduleId: message.scheduleId ?? null,
                commentId: message.id,
            })
        } catch (e) {
            errorSnackbar("Couldn't delete the comment")
        }
    }

    async function edit(message) {
        try {
            const newText = await openEditDialog({ initialBody: message.body ?? "" })
            if (!newText) return
            await editShareLinkScheduleComment({
                shareLinkId: message.shareLinkId ?? shareLinkId,
                regimeId,
                scheduleId: message.scheduleId ?? null,
                commentId: message.id,
                content: newText,
            })
        } catch (e) {
            errorSnackbar("Couldn't save your changes")
        }
    }
}
